import React, { useState } from "react";
import BootstrapTable, {
    BootstrapTableProps,
    ColumnDescription,
    PaginationOptions,
} from "react-bootstrap-table-next";
import paginationFactory, {
    PaginationListStandalone,
    PaginationProvider,
    SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import { useForm } from "react-hook-form";
import { ErrorBoundary } from "../ErrorBoundary";

import "./styles.css";

let table = 0;
const { SearchBar } = Search;

interface IEditCell {
    onEdit: (cell: string, row: any) => Promise<void>;
}
export interface ITableView {
    columns: Array<ColumnDescription>;
    onViewChange?: () => void;
    sizePerPage?: number;
    cancelYScroll?: boolean;
    defaultStyle?: boolean;
    defaultClasses?: string;
    noSort?: boolean;
    striped?: boolean;
    hover?: boolean;
}
export interface ITableProps extends BootstrapTableProps {
    totalPages?: number;
    noHover?: boolean;
    sizePerPage?: number;
    loading?: boolean;
    noSearch?: boolean;
    controlSizePerPage?: Array<number>;
    edit?: IEditCell;
    paginationColor?: "Cobranca";
    noPagination?: boolean;
    tableView?: ITableView;
    defaultStyle?: {
        classes?: string;
        align?: "center" | "left" | "right" | "justify";
    };
    center?: boolean;
}
interface IInternalTableProps extends ITableProps {
    view: "table" | "card";
    setView: (view: "table" | "card") => void;
}
interface IEditCellFormatterProps {
    cell: string;
    row: any;
    onEdit?: (cell: string, row: any) => Promise<void>;
}
interface IEditCellFormData {
    cell: string;
}
function EditCellFormatter(props: IEditCellFormatterProps) {
    const [editting, setEditting] = useState(false);
    const [cell, setCell] = useState(props.cell);
    const { register, getValues } = useForm<IEditCellFormData>();

    return (
        <div
            onClick={() => {
                setEditting(true);
            }}
        >
            {editting ? (
                <input
                    type="text"
                    className="form-control"
                    name="cell"
                    ref={register()}
                    defaultValue={props.cell}
                    autoFocus
                    autoComplete="off"
                    onBlur={() => {
                        const cell = getValues("cell");
                        if (props.onEdit && cell !== props.cell) {
                            props.onEdit(cell, props.row).finally(() => {
                                setEditting(false);
                                setCell(cell);
                            });
                        } else setEditting(false);
                    }}
                />
            ) : (
                cell
            )}
        </div>
    );
}

/**
 * @param totalPages - usado para indicar total de páginas
 * de uma tabela com dados sob demanda
 * @param noHover - tira o hover das linhas da tabela
 * @param sizePerPage - tamanho da página (default é 10)
 * @param loading - variável booleana para controlar se a tabela está
 * carregando ou não ( passar um state )
 * @param noSearch - Tira a barra de pesquisa
 * @param controlSizePerPage - vetor com as opções para o usuário selecionar
 * o tamanho da página
 * @param edit - parametro para fazer tabela com dados editáveis, passando
 * um onEdit para ser exeutado para fazer essa edição
 * @param paginationColor - cor customizada da paginação
 * @param noPagination - tira a paginação
 * @param tableView - parâmetro que mostra se a tabela tem dois modos de vizualização.
 * Passando o .columns, igual ao passado para o componente Table padrão, irá gerar
 * uma view pré formatada de tabela, com scroll e mostrando todos dados. Passando
 * o .onViewChange será executada uma função na troca de contexto card <-> table.
 * Os dataField não podem se repetir entre os columns pois isso eles não são
 * substituidos na troca ( na verdade isso deve ser averiguado ).
 *
 * Recebe todos as props do componente da lib react-bootstrap-table-2
 *
 * docs: https://react-bootstrap-table.github.io/react-bootstrap-table2/docs/table-props.html
 *
 * @returns Componente de tabela que usa o React Bootstrap Table 2
 *
 * @todo Arrumar problema de troca de contexto com columns de mesmo nome
 * ( testar se ainda dá o problema )
 */
export default function Table(componentProps: ITableProps) {
    //controle de table view - state e localstorage para preferência
    const DEFAULT_TABLE_VIEW =
        window.localStorage.getItem("DEFAULT_TABLE_VIEW");
    const [view, setView] = useState<"card" | "table">(
        DEFAULT_TABLE_VIEW === "card" || DEFAULT_TABLE_VIEW === "table"
            ? DEFAULT_TABLE_VIEW
            : "table"
    );

    // aplica default style config na table view ( config default de tableview )
    if (
        componentProps.tableView &&
        (componentProps.tableView.defaultStyle ||
            componentProps.tableView.defaultClasses ||
            !componentProps.tableView.noSort)
    ) {
        componentProps.tableView.columns.forEach((column) => {
            if (!componentProps.tableView?.noSort) {
                column.sort = column.sort ?? true;
                column.headerClasses = `${column.headerClasses} cursor-pointer`;
            }
            if (componentProps.tableView?.defaultClasses)
                column.classes =
                    column.classes ?? componentProps.tableView?.defaultClasses;
            if (componentProps.tableView?.defaultStyle) {
                column.align = column.align ?? "center";
                column.headerAlign = column.headerAlign ?? "center";
            }
        });
    }

    if (componentProps.defaultStyle) {
        componentProps.columns.forEach((column) => {
            if (componentProps.defaultStyle?.classes)
                column.classes =
                    column.classes ?? componentProps.defaultStyle?.classes;
            if (componentProps.defaultStyle?.align) {
                column.align =
                    column.align ?? componentProps.defaultStyle.align;
                column.headerAlign =
                    column.headerAlign ?? componentProps.defaultStyle.align;
            }
        });
    }

    // if (componentProps.center) {
    //     componentProps.classes = 'v-middle-cells'
    // }

    return view === "table" && componentProps.tableView ? (
        <InnerTableComponent
            {...componentProps}
            columns={componentProps.tableView.columns}
            setView={setView}
            classes={
                componentProps.center
                    ? `v-middle-cells ${componentProps.classes}`
                    : componentProps.classes
            }
            view="table"
        />
    ) : (
        <InnerTableComponent
            {...componentProps}
            classes={
                componentProps.center
                    ? `v-middle-cells ${componentProps.classes}`
                    : componentProps.classes
            }
            setView={setView}
            view="card"
        />
    );
}

// Alteração para Inner Table foi feita para impedir bug que acontecia na troca
// de cotexto 'table' para 'view', quando haviam colunas com o mesmo nome, elas
// não eram alteradas. Por isso agora é feita a renderização de componentes de tabela
// diferentes
function InnerTableComponent(componentProps: IInternalTableProps) {
    //controle de table view - state e localstorage para preferência
    const { view, setView } = componentProps;

    //paginação
    const options: PaginationOptions = {
        paginationSize: 3,
        pageStartIndex: 1,
        firstPageText: "Primeira",
        prePageText: "Voltar",
        nextPageText: "Próximo",
        lastPageText: "Última",
        custom: true,
        totalSize: componentProps.totalPages
            ? componentProps.totalPages * 10
            : componentProps.data.length,
        sizePerPageList:
            componentProps.controlSizePerPage ||
            (componentProps.sizePerPage ? [componentProps.sizePerPage] : [10]),
        // se a table view estiver ativada, sizeperpage será o número de dados ( ou o valor customizado na props tableView.sizePerPage )
        sizePerPage:
            view === "table" && !!componentProps.tableView
                ? componentProps.tableView.sizePerPage ||
                  ((componentProps.remote as any)?.pagination
                      ? 10
                      : componentProps.data.length) ||
                  1
                : componentProps.sizePerPage,
    };

    const OverlayRender = (loading: boolean) => (loading ? LoadingDiv : "div");

    //cell edit, se for passado o parâmetro, muda o formatter das colunas editáveis
    if (componentProps.edit) {
        componentProps.columns.forEach((c, i) => {
            if (c.editable)
                componentProps.columns[i].formatter = (cell, row) => (
                    <EditCellFormatter
                        cell={cell}
                        row={row}
                        onEdit={componentProps.edit?.onEdit}
                    />
                );
        });
    }

    return (
        <ToolkitProvider {...componentProps} search>
            {(props) => (
                <div>
                    {!componentProps.noSearch && (
                        <>
                            <SearchBar
                                {...props.searchProps}
                                className="searchTable"
                                placeholder="Pesquisar"
                                tableId={"" + table++}
                            />
                            <hr />
                        </>
                    )}

                    <PaginationProvider pagination={paginationFactory(options)}>
                        {({ paginationProps, paginationTableProps }) => (
                            <div>
                                {
                                    // botão para troca de contexto card <-> table
                                    componentProps.tableView && (
                                        <div className="card-toolbar d-flex justify-content-end">
                                            <div className="dropdown dropleft">
                                                <button
                                                    type="button"
                                                    className="btn btn-hover-light-primary btn-icon btn-sm"
                                                    data-toggle="dropdown"
                                                    aria-haspopup="true"
                                                    aria-expanded="false"
                                                >
                                                    <i className="ki ki-more-hor"></i>
                                                </button>
                                                <div className="dropdown-menu dropdown-menu-sm">
                                                    <button
                                                        type="button"
                                                        className="dropdown-item"
                                                        onClick={() => {
                                                            if (
                                                                componentProps
                                                                    .tableView
                                                                    ?.onViewChange
                                                            )
                                                                componentProps.tableView?.onViewChange();
                                                            window.localStorage.setItem(
                                                                "DEFAULT_TABLE_VIEW",
                                                                view === "card"
                                                                    ? "table"
                                                                    : "card"
                                                            );
                                                            setView(
                                                                view === "card"
                                                                    ? "table"
                                                                    : "card"
                                                            );
                                                        }}
                                                    >
                                                        {view === "card"
                                                            ? "Visualizar como tabela"
                                                            : "Visualizar como card"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }

                                <ErrorBoundary errorText="Tivemos um problema ao tentar te mostrar a tabela :(">
                                    {view === "table" &&
                                    componentProps.tableView ? (
                                        <BootstrapTable
                                            {...props.baseProps}
                                            {...paginationTableProps}
                                            {...componentProps}
                                            // no tableview ativado o tableview.columns é usado
                                            columns={
                                                componentProps.tableView.columns
                                            }
                                            // estilização garantida do tableview:
                                            // com hover e listrado, classes que fazem o scroll, uma altura máxima e header colado no topo
                                            hover={
                                                componentProps.tableView
                                                    .hover ?? true
                                            }
                                            striped={
                                                componentProps.tableView
                                                    .striped ?? true
                                            }
                                            wrapperClasses={`${
                                                componentProps.wrapperClasses ??
                                                ""
                                            } mw-100 ${
                                                componentProps.tableView
                                                    .cancelYScroll
                                                    ? ""
                                                    : "maxHeight-300"
                                            } custom-scroll mb-5`}
                                            headerClasses={`${
                                                componentProps.headerClasses ??
                                                ""
                                            }  sticky-thead`}
                                            rowClasses={
                                                typeof componentProps.rowClasses ===
                                                "function"
                                                    ? componentProps.rowClasses
                                                    : `${
                                                          componentProps.rowClasses ??
                                                          ""
                                                      } rowTableView`
                                            }
                                            overlay={OverlayRender}
                                        />
                                    ) : (
                                        <BootstrapTable
                                            {...props.baseProps}
                                            {...paginationTableProps}
                                            {...componentProps}
                                            columns={props.baseProps.columns}
                                            hover={
                                                componentProps.hover ??
                                                !componentProps.noHover
                                            }
                                            overlay={OverlayRender}
                                        />
                                    )}
                                </ErrorBoundary>

                                {!componentProps.noPagination && (
                                    <div
                                        className={`row ${componentProps.paginationColor}Pagination`}
                                    >
                                        <div className="col-2 pl-5">
                                            <SizePerPageDropdownStandalone
                                                {...paginationProps}
                                                hidden={
                                                    paginationProps
                                                        .sizePerPageList
                                                        ?.length === 1 ||
                                                    componentProps.data
                                                        .length === 0
                                                }
                                            />
                                        </div>
                                        <div
                                            className={
                                                !componentProps.controlSizePerPage ||
                                                componentProps
                                                    .controlSizePerPage
                                                    .length <= 1
                                                    ? "col-12"
                                                    : "col-10"
                                            }
                                        >
                                            <PaginationListStandalone
                                                {...paginationProps}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </PaginationProvider>
                </div>
            )}
        </ToolkitProvider>
    );
}

//overlay
const LoadingDiv = () => {
    return (
        <div className="d-flex align-items-center justify-content-center my-10">
            <div
                className="spinner-border spinner-border-lg text-primary"
                role="status"
            >
                <span className="sr-only">Carregando...</span>
            </div>
        </div>
    );
};
